/**
 * Predictive Care Analytics Dashboard
 * 
 * AI-driven predictive alerts for hospice eligibility and care planning.
 * Analyzes functional decline trends to predict when patients will cross thresholds.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Area, ComposedChart
} from "recharts";
import {
  Brain, TrendingDown, AlertTriangle, Clock, User, 
  ChevronRight, Loader2, Sparkles, Calendar, Target,
  Activity, Bell, RefreshCw, Filter, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Risk level colors
const RISK_COLORS = {
  critical: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" },
  high: { bg: "bg-orange-500/20", text: "text-orange-400", border: "border-orange-500/30" },
  moderate: { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30" },
  low: { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" }
};

// Custom tooltip for trajectory chart
const TrajectoryTooltip = ({ active, payload, isDark }) => {
  if (!active || !payload || !payload.length) return null;
  
  const data = payload[0].payload;
  const isPredicted = data.type === "predicted";
  
  return (
    <div className={`p-3 rounded-lg shadow-lg border ${
      isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"
    }`}>
      <p className={`text-xs mb-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
        {isPredicted ? "Predicted" : "Historical"} - Day {data.day}
      </p>
      <div className="space-y-1">
        <p className={`text-sm ${data.kps <= 70 ? "text-amber-400" : isDark ? "text-white" : "text-gray-900"}`}>
          <strong>KPS:</strong> {data.kps}
        </p>
        <p className={`text-sm ${data.pps <= 70 ? "text-amber-400" : isDark ? "text-white" : "text-gray-900"}`}>
          <strong>PPS:</strong> {data.pps}%
        </p>
      </div>
    </div>
  );
};

export default function PredictiveAnalyticsDashboard({ 
  isDark = true, 
  compact = false 
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  // States
  const [predictions, setPredictions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [riskFilter, setRiskFilter] = useState("all");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [trajectory, setTrajectory] = useState(null);
  const [loadingTrajectory, setLoadingTrajectory] = useState(false);
  const [showDetailSheet, setShowDetailSheet] = useState(false);

  // Fetch predictions
  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        setLoading(true);
        const filterParam = riskFilter !== "all" ? `?risk_filter=${riskFilter}` : "";
        const res = await fetch(`${API_URL}/api/predictive-analytics/predictions${filterParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setPredictions(data.predictions || []);
          setSummary(data.summary);
        }
      } catch (error) {
        console.error("Failed to fetch predictions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPredictions();
  }, [token, riskFilter]);

  // Fetch patient trajectory
  const fetchTrajectory = async (patientId) => {
    try {
      setLoadingTrajectory(true);
      const res = await fetch(`${API_URL}/api/predictive-analytics/patient/${patientId}/trajectory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setTrajectory(await res.json());
      }
    } catch (error) {
      console.error("Failed to fetch trajectory:", error);
    } finally {
      setLoadingTrajectory(false);
    }
  };

  // Handle patient selection
  const handlePatientClick = (prediction) => {
    setSelectedPatient(prediction);
    fetchTrajectory(prediction.patient_id);
    setShowDetailSheet(true);
  };

  // Get risk badge
  const getRiskBadge = (riskLevel) => {
    const colors = RISK_COLORS[riskLevel] || RISK_COLORS.low;
    return (
      <Badge className={`${colors.bg} ${colors.text} border ${colors.border}`}>
        {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)}
      </Badge>
    );
  };

  // Compact view for widget
  if (compact) {
    return (
      <div className="space-y-3" data-testid="predictive-analytics-compact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-violet-400" />
            <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              AI Predictions
            </span>
          </div>
          <Badge variant="outline" className="text-xs bg-violet-500/10 text-violet-400">
            <Sparkles className="w-3 h-3 mr-1" />
            Live
          </Badge>
        </div>

        {loading ? (
          <div className={`p-4 rounded-lg text-center ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
          </div>
        ) : summary ? (
          <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            {/* Risk distribution mini bars */}
            <div className="space-y-2 mb-3">
              {summary.critical_count > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-600"}`}>Critical</span>
                  </div>
                  <span className="text-sm font-bold text-red-400">{summary.critical_count}</span>
                </div>
              )}
              {summary.high_count > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-400" />
                    <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-600"}`}>High Risk</span>
                  </div>
                  <span className="text-sm font-bold text-orange-400">{summary.high_count}</span>
                </div>
              )}
              {summary.moderate_count > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                    <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-600"}`}>Moderate</span>
                  </div>
                  <span className="text-sm font-bold text-amber-400">{summary.moderate_count}</span>
                </div>
              )}
            </div>

            {/* Top prediction */}
            {predictions.length > 0 && (
              <div 
                className={`p-2 rounded border cursor-pointer hover:opacity-80 ${
                  RISK_COLORS[predictions[0].risk_level]?.border || "border-slate-700"
                } ${isDark ? "bg-slate-900" : "bg-white"}`}
                onClick={() => handlePatientClick(predictions[0])}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                    {predictions[0].patient_name}
                  </span>
                  {getRiskBadge(predictions[0].risk_level)}
                </div>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  {predictions[0].days_to_threshold !== null 
                    ? `~${predictions[0].days_to_threshold}d to threshold`
                    : `KPS: ${predictions[0].current_kps}`
                  }
                </p>
              </div>
            )}

            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full mt-2 text-xs"
              onClick={() => setShowDetailSheet(true)}
            >
              View All Predictions
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        ) : null}

        {/* Detail Sheet */}
        <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
          <SheetContent side="right" className={`w-[700px] ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}>
            <SheetHeader>
              <SheetTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                <Brain className="w-5 h-5 text-violet-400" />
                Predictive Care Analytics
                <Badge className="bg-violet-500/20 text-violet-400 ml-2">
                  <Sparkles className="w-3 h-3 mr-1" />
                  AI Powered
                </Badge>
              </SheetTitle>
              <SheetDescription>
                AI-driven predictions for hospice eligibility based on functional decline trends
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4">
              {/* Filter */}
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={riskFilter} onValueChange={setRiskFilter}>
                  <SelectTrigger className={`w-36 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}>
                    <SelectValue placeholder="Filter by risk" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Risks</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Predictions List */}
              <ScrollArea className="h-[calc(100vh-250px)]">
                <div className="space-y-3 pr-4">
                  {predictions.map((prediction, idx) => (
                    <Card 
                      key={idx}
                      className={`cursor-pointer transition-all hover:scale-[1.01] ${
                        isDark ? "bg-slate-800 border-slate-700 hover:border-slate-600" : "bg-white hover:bg-gray-50"
                      } ${selectedPatient?.patient_id === prediction.patient_id ? "ring-2 ring-violet-500" : ""}`}
                      onClick={() => handlePatientClick(prediction)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                              {prediction.patient_name}
                            </p>
                            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                              ID: {prediction.patient_id}
                            </p>
                          </div>
                          {getRiskBadge(prediction.risk_level)}
                        </div>

                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div>
                            <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Current KPS</p>
                            <p className={`text-lg font-bold ${prediction.current_kps <= 70 ? "text-amber-400" : "text-blue-400"}`}>
                              {prediction.current_kps}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>30-Day Predicted</p>
                            <p className={`text-lg font-bold ${prediction.predicted_kps_30d <= 70 ? "text-amber-400" : "text-slate-300"}`}>
                              {prediction.predicted_kps_30d}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Days to Threshold</p>
                            <p className={`text-lg font-bold ${
                              prediction.days_to_threshold !== null && prediction.days_to_threshold <= 14 
                                ? "text-red-400" 
                                : prediction.days_to_threshold !== null && prediction.days_to_threshold <= 30
                                  ? "text-orange-400"
                                  : "text-emerald-400"
                            }`}>
                              {prediction.days_to_threshold !== null ? `~${prediction.days_to_threshold}d` : "N/A"}
                            </p>
                          </div>
                        </div>

                        <div className={`p-2 rounded text-xs ${isDark ? "bg-slate-900" : "bg-gray-100"}`}>
                          <p className={isDark ? "text-slate-300" : "text-gray-700"}>
                            <strong>AI Recommendation:</strong> {prediction.recommendation}
                          </p>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1">
                            <TrendingDown className="w-3 h-3 text-red-400" />
                            <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                              {prediction.decline_rate} pts/week
                            </span>
                          </div>
                          <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                            {Math.round(prediction.confidence * 100)}% confidence
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </SheetContent>
        </Sheet>

        {/* Patient Trajectory Sheet */}
        <Sheet open={!!trajectory} onOpenChange={() => setTrajectory(null)}>
          <SheetContent side="right" className={`w-[800px] ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}>
            {loadingTrajectory ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
              </div>
            ) : trajectory ? (
              <>
                <SheetHeader>
                  <SheetTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                    <Activity className="w-5 h-5 text-blue-400" />
                    {trajectory.patient_name} - Trajectory Analysis
                  </SheetTitle>
                  <SheetDescription>
                    {trajectory.diagnosis}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-4 space-y-4">
                  {/* Current Status */}
                  <div className="grid grid-cols-4 gap-3">
                    <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                      <CardContent className="p-3 text-center">
                        <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Current KPS</p>
                        <p className={`text-2xl font-bold ${trajectory.current_status.kps <= 70 ? "text-amber-400" : "text-blue-400"}`}>
                          {trajectory.current_status.kps}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                      <CardContent className="p-3 text-center">
                        <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>7-Day Pred.</p>
                        <p className={`text-2xl font-bold ${trajectory.predictions.kps_7d <= 70 ? "text-amber-400" : "text-slate-300"}`}>
                          {trajectory.predictions.kps_7d}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                      <CardContent className="p-3 text-center">
                        <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>30-Day Pred.</p>
                        <p className={`text-2xl font-bold ${trajectory.predictions.kps_30d <= 70 ? "text-amber-400" : "text-slate-300"}`}>
                          {trajectory.predictions.kps_30d}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                      <CardContent className="p-3 text-center">
                        <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Risk Level</p>
                        <div className="mt-1">
                          {getRiskBadge(trajectory.current_status.risk_level)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Trajectory Chart */}
                  <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Functional Trajectory</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={trajectory.trajectory}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} />
                            <XAxis 
                              dataKey="day" 
                              tick={{ fill: isDark ? "#94a3b8" : "#6b7280", fontSize: 10 }}
                              label={{ value: "Days", position: "bottom", fill: isDark ? "#94a3b8" : "#6b7280", fontSize: 10 }}
                            />
                            <YAxis 
                              domain={[0, 100]}
                              tick={{ fill: isDark ? "#94a3b8" : "#6b7280", fontSize: 10 }}
                            />
                            <Tooltip content={<TrajectoryTooltip isDark={isDark} />} />
                            <ReferenceLine y={70} stroke="#f59e0b" strokeDasharray="5 5" />
                            <ReferenceLine x={0} stroke="#6366f1" strokeDasharray="3 3" />
                            <Line 
                              type="monotone" 
                              dataKey="kps" 
                              stroke="#3b82f6" 
                              strokeWidth={2}
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                return (
                                  <circle 
                                    cx={cx} 
                                    cy={cy} 
                                    r={payload.type === "predicted" ? 3 : 5}
                                    fill={payload.type === "predicted" ? "transparent" : "#3b82f6"}
                                    stroke="#3b82f6"
                                    strokeWidth={2}
                                    strokeDasharray={payload.type === "predicted" ? "3 3" : "0"}
                                  />
                                );
                              }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex justify-center gap-4 mt-2 text-xs">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-blue-500" />
                          <span className={isDark ? "text-slate-400" : "text-gray-500"}>Historical</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full border-2 border-blue-500 border-dashed" />
                          <span className={isDark ? "text-slate-400" : "text-gray-500"}>Predicted</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-0.5 bg-amber-500" style={{ backgroundImage: "linear-gradient(90deg, #f59e0b 50%, transparent 50%)", backgroundSize: "8px 100%" }} />
                          <span className={isDark ? "text-slate-400" : "text-gray-500"}>Hospice Threshold (70)</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* AI Recommendation */}
                  <Card className={`border-l-4 border-l-violet-500 ${isDark ? "bg-violet-500/10 border-violet-500/30" : "bg-violet-50"}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-violet-400 mt-0.5" />
                        <div>
                          <p className={`text-sm font-medium mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>
                            AI Care Recommendation
                          </p>
                          <p className={`text-sm ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                            {trajectory.recommendation}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Milestones */}
                  {trajectory.milestones?.length > 0 && (
                    <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Target className="w-4 h-4 text-amber-400" />
                          Care Milestones
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {trajectory.milestones.map((milestone, idx) => (
                            <div key={idx} className={`p-2 rounded flex items-start gap-2 ${isDark ? "bg-slate-900" : "bg-gray-100"}`}>
                              <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                              <div>
                                <p className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                                  {milestone.event}
                                  {milestone.day !== 0 && (
                                    <span className="text-amber-400 ml-2">Day {milestone.day}</span>
                                  )}
                                </p>
                                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                                  {milestone.action}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-violet-600 hover:bg-violet-700"
                      onClick={() => {
                        navigate(`/lcd-eligibility?patient=${trajectory.patient_id}`);
                        setTrajectory(null);
                      }}
                    >
                      <Activity className="w-4 h-4 mr-2" />
                      Full LCD Evaluation
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        toast.success("Care planning notification sent to team");
                      }}
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Notify Team
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Full dashboard view
  return (
    <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="predictive-analytics-dashboard">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Brain className="w-5 h-5 text-violet-400" />
              Predictive Care Analytics
              <Badge className="bg-violet-500/20 text-violet-400 ml-2">
                <Sparkles className="w-3 h-3 mr-1" />
                AI Powered
              </Badge>
            </CardTitle>
            <CardDescription>
              AI-driven predictions for hospice eligibility based on functional decline trends
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-4 gap-4">
                <Card className={`border-l-4 border-l-red-500 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}>
                  <CardContent className="p-4">
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Critical Risk</p>
                    <p className="text-3xl font-bold text-red-400">{summary.critical_count}</p>
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Immediate action needed</p>
                  </CardContent>
                </Card>
                <Card className={`border-l-4 border-l-orange-500 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}>
                  <CardContent className="p-4">
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>High Risk</p>
                    <p className="text-3xl font-bold text-orange-400">{summary.high_count}</p>
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Care planning review</p>
                  </CardContent>
                </Card>
                <Card className={`border-l-4 border-l-amber-500 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}>
                  <CardContent className="p-4">
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Moderate Risk</p>
                    <p className="text-3xl font-bold text-amber-400">{summary.moderate_count}</p>
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Close monitoring</p>
                  </CardContent>
                </Card>
                <Card className={`border-l-4 border-l-emerald-500 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}>
                  <CardContent className="p-4">
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Low Risk</p>
                    <p className="text-3xl font-bold text-emerald-400">{summary.low_count}</p>
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Standard care</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Predictions Table */}
            <div className="space-y-2">
              {predictions.slice(0, 5).map((prediction, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-[1.005] ${
                    isDark ? "bg-slate-800 border-slate-700 hover:border-slate-600" : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                  onClick={() => handlePatientClick(prediction)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${RISK_COLORS[prediction.risk_level]?.bg || "bg-slate-700"}`}>
                        <User className={`w-5 h-5 ${RISK_COLORS[prediction.risk_level]?.text || "text-slate-400"}`} />
                      </div>
                      <div>
                        <p className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{prediction.patient_name}</p>
                        <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                          KPS: {prediction.current_kps} → {prediction.predicted_kps_30d} (30d)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {prediction.days_to_threshold !== null && (
                        <div className="text-right">
                          <p className={`text-sm font-bold ${
                            prediction.days_to_threshold <= 14 ? "text-red-400" : "text-amber-400"
                          }`}>
                            ~{prediction.days_to_threshold} days
                          </p>
                          <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>to threshold</p>
                        </div>
                      )}
                      {getRiskBadge(prediction.risk_level)}
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
